/**
 * Slug transliteration/derivation helper — PIM Wave P1 Task 3.
 *
 * No existing slugify/transliteration util was found anywhere under
 * `apps/web` or `packages` (grepped for slugify/transliterat/cyrillic before
 * writing this — the only hits were Cyrillic-content test fixtures and SMS/
 * password-policy docs, nothing reusable). This is a small local pure helper.
 *
 * DEVIATION FROM THE TASK BRIEF, called out explicitly: the brief's own text
 * (line 20) names the slug pattern as kebab-case
 * `^[a-z0-9]+(?:-[a-z0-9]+)*$`. The ACTUAL backend contract — read directly
 * from `apps/api/garden/features/catalog_schema/schemas.py:60` —
 * is `_SLUG_PATTERN = r"^[a-z][a-z0-9_]*$"` (must start with a letter,
 * lowercase letters/digits/UNDERSCORE only, NO hyphen), shared by
 * `group.slug` / `field.slug` / `option.value_slug`. Recon
 * (`.superwers/sdd/p1-task0-recon.md` §A14) already records this exact
 * regex. Building the kebab-case pattern from the brief would produce
 * auto-derived slugs containing hyphens that the backend rejects with a 422
 * on every create — so this module (and the Zod schema in
 * `components/FieldFormDrawer.tsx`) follows the verified backend regex, not
 * the brief's literal text. Same resolution principle the brief itself uses
 * for the list-columns spec ("recon truth beats design-notes wishlist").
 */

const FIELD_SLUG_PATTERN = /^[a-z][a-z0-9_]*$/;
const FIELD_SLUG_MAX_LEN = 64;

/** Cyrillic (RU + Kyrgyz-specific letters) -> ASCII transliteration table. */
const CYRILLIC_TO_LATIN: Readonly<Record<string, string>> = {
  а: "a",
  б: "b",
  в: "v",
  г: "g",
  д: "d",
  е: "e",
  ё: "e",
  ж: "zh",
  з: "z",
  и: "i",
  й: "i",
  к: "k",
  л: "l",
  м: "m",
  н: "n",
  о: "o",
  п: "p",
  р: "r",
  с: "s",
  т: "t",
  у: "u",
  ф: "f",
  х: "h",
  ц: "ts",
  ч: "ch",
  ш: "sh",
  щ: "sch",
  ъ: "",
  ы: "y",
  ь: "",
  э: "e",
  ю: "yu",
  я: "ya",
  // Kyrgyz-specific letters (defensive — the RU name field could contain
  // them if an admin pastes mixed-script text).
  ң: "ng",
  ө: "o",
  ү: "u",
};

/** Transliterates Cyrillic letters to ASCII Latin; passes other characters through. */
export function transliterate(text: string): string {
  return text
    .toLowerCase()
    .split("")
    .map((ch) => CYRILLIC_TO_LATIN[ch] ?? ch)
    .join("");
}

/**
 * Derives a backend-valid field/group/option slug from free text (typically
 * the RU name). Output always matches `^[a-z][a-z0-9_]*$`, capped at 64
 * chars — or `""` if the input has no derivable characters.
 */
export function slugify(text: string): string {
  const translit = transliterate(text);
  let slug = translit
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (slug === "") return "";
  if (!/^[a-z]/.test(slug)) slug = `f_${slug}`;
  return slug.slice(0, FIELD_SLUG_MAX_LEN).replace(/_+$/, "");
}

export { FIELD_SLUG_PATTERN, FIELD_SLUG_MAX_LEN };
